import { Box, Skeleton, alpha } from '@mui/material';

export type GatewaySwitchFallbackVariant =
    | 'dashboard'
    | 'list'
    | 'analytics'
    | 'settings'
    | 'generic';

const RoundedSkeleton = ({ height }: { height: number }) => (
    <Skeleton
        variant="rounded"
        animation="wave"
        height={height}
        sx={{ borderRadius: '18px' }}
    />
);

export const GatewaySwitchMenuFallback = ({
    expanded = true,
    itemCount = 5,
}: {
    expanded?: boolean;
    itemCount?: number;
}) => (
    <Box aria-hidden="true" sx={{ width: '100%' }}>
        {Array.from({ length: itemCount }).map((_, index) => (
            <Box
                key={index}
                sx={(theme) => ({
                    width: '100%',
                    minHeight: 46,
                    mb: 0.55,
                    borderRadius: '12px',
                    px: expanded ? 1.05 : 0,
                    py: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: expanded ? 'flex-start' : 'center',
                    direction: 'ltr',
                    color: theme.palette.text.secondary,
                })}
            >
                <Box
                    sx={(theme) => ({
                        width: 30,
                        height: 30,
                        minWidth: 30,
                        flexShrink: 0,
                        marginInlineEnd: expanded ? 0.8 : 0,
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: alpha(theme.palette.text.secondary, 0.055),
                    })}
                >
                    <Skeleton
                        variant="rounded"
                        animation="wave"
                        width={16}
                        height={16}
                        sx={{ borderRadius: '4px' }}
                    />
                </Box>

                {expanded && (
                    <Skeleton
                        variant="rounded"
                        animation="wave"
                        width={72 + (index % 3) * 12}
                        height={13}
                        sx={{
                            marginInlineStart: 0.65,
                            borderRadius: '4px',
                            flexShrink: 0,
                        }}
                    />
                )}
            </Box>
        ))}
    </Box>
);

const HeaderSkeleton = ({ actions = 1 }: { actions?: number }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1.2,
            my: 2,
        }}
    >
        <Skeleton width={190} height={28} />
        {actions > 0 && (
            <Box sx={{ display: 'flex', gap: 1 }}>
                {Array.from({ length: actions }).map((_, index) => (
                    <Skeleton
                        key={index}
                        variant="rounded"
                        width={index === 0 ? 96 : 118}
                        height={36}
                        sx={{ borderRadius: '10px' }}
                    />
                ))}
            </Box>
        )}
    </Box>
);

const DashboardContentSkeleton = () => (
    <>
        <HeaderSkeleton actions={0} />
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, minmax(0, 1fr))',
                    xl: 'repeat(4, minmax(0, 1fr))',
                },
                gap: 1.4,
            }}
        >
            {Array.from({ length: 4 }).map((_, index) => (
                <RoundedSkeleton key={index} height={154} />
            ))}
        </Box>
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: {
                    xs: '1fr',
                    lg: 'minmax(0, 1.62fr) minmax(330px, 0.78fr)',
                },
                gap: 1.8,
                mt: 1.8,
            }}
        >
            <RoundedSkeleton height={410} />
            <RoundedSkeleton height={410} />
        </Box>
        <Box sx={{ mt: 1.8 }}>
            <RoundedSkeleton height={360} />
        </Box>
    </>
);

const ListContentSkeleton = () => (
    <>
        <HeaderSkeleton actions={2} />
        <Box
            sx={(theme) => ({
                overflow: 'hidden',
                borderRadius: '18px',
                border: `1px solid ${theme.palette.border.light}`,
                bgcolor: alpha(
                    theme.palette.background.paper,
                    theme.palette.mode === 'dark' ? 0.82 : 0.9,
                ),
            })}
        >
            <Box sx={{ px: 1.6, py: 1.4 }}>
                <Skeleton
                    variant="rounded"
                    height={48}
                    sx={{ borderRadius: '10px', mb: 0.9 }}
                />
                {Array.from({ length: 8 }).map((_, index) => (
                    <Skeleton
                        key={index}
                        variant="rounded"
                        height={54}
                        sx={{
                            borderRadius: '8px',
                            mb: index === 7 ? 0 : 0.7,
                        }}
                    />
                ))}
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    py: 1.25,
                    borderTop: '1px solid',
                    borderColor: 'border.light',
                }}
            >
                <Skeleton
                    variant="rounded"
                    width={210}
                    height={30}
                    sx={{ borderRadius: '10px' }}
                />
            </Box>
        </Box>
    </>
);

const AnalyticsContentSkeleton = () => (
    <>
        <HeaderSkeleton actions={1} />
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, minmax(0, 1fr))',
                    xl: 'repeat(4, minmax(0, 1fr))',
                },
                gap: 1.4,
            }}
        >
            {Array.from({ length: 4 }).map((_, index) => (
                <RoundedSkeleton key={index} height={158} />
            ))}
        </Box>
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: {
                    xs: '1fr',
                    lg: 'minmax(0, 1.62fr) minmax(330px, 0.78fr)',
                },
                gap: 1.8,
                mt: 1.8,
            }}
        >
            <RoundedSkeleton height={390} />
            <RoundedSkeleton height={390} />
        </Box>
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: {
                    xs: '1fr',
                    lg: 'repeat(2, minmax(0, 1fr))',
                },
                gap: 1.8,
                mt: 1.8,
            }}
        >
            <RoundedSkeleton height={360} />
            <RoundedSkeleton height={360} />
        </Box>
    </>
);

const SettingsContentSkeleton = () => (
    <>
        <HeaderSkeleton actions={0} />
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', lg: '260px minmax(0, 1fr)' },
                gap: 1.8,
            }}
        >
            <RoundedSkeleton height={420} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.4 }}>
                <RoundedSkeleton height={190} />
                <RoundedSkeleton height={260} />
            </Box>
        </Box>
    </>
);

const GenericContentSkeleton = () => (
    <>
        <HeaderSkeleton actions={0} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.6 }}>
            <RoundedSkeleton height={180} />
            <RoundedSkeleton height={360} />
        </Box>
    </>
);

const ContentSkeleton = ({
    variant,
}: {
    variant: GatewaySwitchFallbackVariant;
}) => {
    if (variant === 'dashboard') return <DashboardContentSkeleton />;
    if (variant === 'list') return <ListContentSkeleton />;
    if (variant === 'analytics') return <AnalyticsContentSkeleton />;
    if (variant === 'settings') return <SettingsContentSkeleton />;
    return <GenericContentSkeleton />;
};

export const GatewaySwitchContentFallback = ({
    variant = 'generic',
}: {
    variant?: GatewaySwitchFallbackVariant;
}) => (
    <Box
        role="status"
        aria-live="polite"
        aria-label="در حال آماده‌سازی درگاه"
        sx={{ width: '100%' }}
    >
        <ContentSkeleton variant={variant} />
    </Box>
);
